import moment from 'moment';
import { Strict } from '../../../../types/utilTypes';
import { isValidNum } from "../../../../utilities/typeGuard";
import { AdvertRegulationInput, AdvertRegulationOutput } from './types';
import { yearDiff } from '../../../../utilities/date';

const CONSTANTS = {
  plates: -1180
} as const

type Input = Strict<AdvertRegulationInput>

function validateInput(input: AdvertRegulationInput): asserts input is Input {
  if (typeof input.evaluationVehicleFirstRegDate !== 'string') {
    throw new Error(`input.evaluationVehicleFirstRegDate must be a string, instead it was ${input.evaluationVehicleFirstRegDate}. Cannot calculate advert regulation`)
  }
  if (typeof input.firstRegDate !== 'string') {
    throw new Error(`input.firstRegDate must be a string, instead it was ${input.firstRegDate}. Cannot calculate advert regulation`)
  }
  if (!(isValidNum(input.salesPrice) && input.salesPrice > 0)) {
    throw new Error(`input.salesPrice must be a non-zero number, instead it was ${input.salesPrice}. Cannot calculate advert regulation`)
  }
  if (!(isValidNum(input.mileage) && input.mileage > -1)) {
    throw new Error(`input.mileage must be a non-negative number, instead it was ${input.mileage}. Cannot calculate advert regulation`)
  }
}

export default function calculateAdvertRegulation(input: AdvertRegulationInput): AdvertRegulationOutput {
  validateInput(input)
  const { ageRegulatedPrice, ageRegulatedPriceDifference } = calculateAgeRegulatedPrice(input)
  let { advertMileagePerYear, ageRegulatedMileage, ageRegulatedMileageDifference } = calculateAgeRegulatedMileage(input)
  const plates = calculatePlates(input)
  let additionalRegulations = calculateAdditionRegulations(input)

  const totalRegulation = ageRegulatedPriceDifference + plates + additionalRegulations;
  let tradePriceLevel = ageRegulatedPrice + plates + additionalRegulations

  return {
    tradePriceLevel,
    totalRegulation,
    advertMileagePerYear, ageRegulatedMileage, ageRegulatedMileageDifference,
    ageRegulatedPrice, ageRegulatedPriceDifference,
    plates,
    additionalRegulations,
  }
}

function calcRegulatedPrice(price: number, ageDifference: number) {
  const exponent = Math.abs(ageDifference);
  const factor = ageDifference / Math.abs(ageDifference)
  return price * ((1 + (0.1 * factor)) ** exponent);
}

function calculateAgeRegulatedPrice(input: Input) {
  const ageDifference = yearDiff(input.evaluationVehicleFirstRegDate, input?.firstRegDate)
  let ageRegulatedPrice = 0;
  let ageRegulatedPriceDifference = 0

  if (input.overrides?.ageRegulation?.active) {
    if (isValidNum(input.overrides.ageRegulation.price)) {
      ageRegulatedPriceDifference = input.overrides.ageRegulation.price
      ageRegulatedPrice = input.salesPrice + ageRegulatedPriceDifference;
    } else {
      ageRegulatedPrice = calcRegulatedPrice(input.salesPrice, ageDifference)
      ageRegulatedPriceDifference = ageRegulatedPrice - input.salesPrice
    }
  } else {
    ageRegulatedPrice = input.salesPrice
    ageRegulatedPriceDifference = 0
  }

  return { ageRegulatedPrice, ageRegulatedPriceDifference }
}


/** Age-regulated mileage is computed by annualizing the
 * advert vehicle’s mileage (dividing by its age) and then
 * rescaling that annualized rate by the evaluation vehicle’s age.
 * */
function calculateAgeRegulatedMileage(input: Input) {
  const advertAgeYears = input.firstRegDate ? yearDiff(new Date(), input.firstRegDate) || 1 : 1;
  const evalAgeYears = input.evaluationVehicleFirstRegDate ? yearDiff(new Date(), input.evaluationVehicleFirstRegDate) || 1 : 1;
  const advertMileagePerYear = (input.mileage / advertAgeYears);

  let ageRegulatedMileage = 0;
  let ageRegulatedMileageDifference = 0;
  if (input.overrides.ageRegulation.active) {
    if (isValidNum(input.overrides.ageRegulation.mileage)) {
      ageRegulatedMileageDifference = input.overrides.ageRegulation.mileage;
      ageRegulatedMileage = input.mileage + ageRegulatedMileageDifference;
    } else {
      ageRegulatedMileage = (advertMileagePerYear * evalAgeYears);
      ageRegulatedMileageDifference = ageRegulatedMileage - input.mileage;
    }
  } else {
    ageRegulatedMileage = input.mileage;
    ageRegulatedMileageDifference = 0;
  }

  return {
    advertMileagePerYear, ageRegulatedMileage, ageRegulatedMileageDifference
  }
}

function calculatePlates(input: Input) {
  let plates = 0;
  if (input.overrides.plates.active) {
    plates = input.overrides.plates.price ?? CONSTANTS.plates
  }
  return plates
}


function calculateAdditionRegulations(input: Input) {
  let sum = 0;
  input.overrides.additionalRegulations.forEach(item => {
    if (item.active) {
      sum += item?.price ?? 0;
    }
  })
  return sum;
}





